<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-5">
      <div class="text-xl font-bold text-gray-800 dark:text-gray-100">用户管理</div>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        查看租户用户列表、订阅状态，并管理教师角色。若教师仍绑定学生，则不能直接撤销教师身份。
      </p>
    </div>

    <!-- Statistics Cards -->
    <div class="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2 lg:grid-cols-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">总用户数</p>
        <p class="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{{ total }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">教师数量</p>
        <p class="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">{{ teacherCount }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">有效订阅用户</p>
        <p class="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{{ activeSubscriptionCount }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
        <p class="text-sm text-gray-500 dark:text-gray-400">本月新增用户</p>
        <p class="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">{{ newUsersThisMonth }}</p>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="mb-4 flex flex-wrap items-center gap-4">
      <div class="flex-1">
        <input
          v-model.trim="query"
          type="text"
          placeholder="搜索用户名或手机号"
          class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          @keyup.enter="loadUsers(1)"
        />
      </div>
      <select
        v-model="roleFilter"
        class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
      >
        <option value="all">全部用户</option>
        <option value="teacher">仅教师</option>
        <option value="student">仅学生</option>
      </select>
      <select
        v-model="subscriptionFilter"
        class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
      >
        <option value="all">全部</option>
        <option value="active">有订阅</option>
        <option value="none">无订阅</option>
        <option value="expired">已过期</option>
      </select>
      <button class="primary-btn" type="button" @click="loadUsers(1)">刷新</button>
    </div>

    <!-- Status Message -->
    <div v-if="statusMessage" class="mb-4 rounded-lg px-4 py-3 text-sm" :class="statusType === 'success'
      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
      : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300'">
      {{ statusMessage }}
    </div>

    <!-- Users Table -->
    <div class="rounded-lg bg-white shadow dark:bg-gray-800">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">用户名</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">手机号</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">角色</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">当前套餐</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">订阅状态</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">绑定时间</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-indigo-50 dark:hover:bg-gray-700/60">
              <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{{ user.id }}</td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ user.username }}</td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ user.phone || '-' }}</td>
              <td class="px-4 py-4 text-sm">
                <span :class="roleBadgeClass(user.role)">{{ roleLabel(user.role) }}</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">
                <span v-if="user.activePlans.length">{{ user.activePlans.map((plan) => plan.name).join('、') }}</span>
                <span v-else class="text-gray-400 dark:text-gray-500">无套餐</span>
              </td>
              <td class="px-4 py-4 text-sm">
                <span v-if="user.activeSubscription" :class="subscriptionBadgeClass(user.activeSubscription.status)">
                  {{ subscriptionLabel(user.activeSubscription.status) }}
                </span>
                <span v-else class="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  无订阅
                </span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ formatDate(user.boundAt) }}</td>
              <td class="px-4 py-4 text-sm">
                <button
                  v-if="user.role !== 3"
                  class="primary-btn text-sm"
                  type="button"
                  :disabled="busyUserId === user.id"
                  :title="isDemo ? '演示模式下不可操作' : ''"
                  @click="handleAssign(user.id)"
                >
                  设为教师
                </button>
                <button
                  v-else
                  class="danger-btn text-sm"
                  type="button"
                  :disabled="busyUserId === user.id"
                  :title="isDemo ? '演示模式下不可操作' : ''"
                  @click="handleRevoke(user.id)"
                >
                  撤销教师
                </button>
              </td>
            </tr>
            <tr v-if="!filteredUsers.length">
              <td colspan="8" class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">当前没有可展示的用户</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between border-t border-gray-200 px-4 py-4 dark:border-gray-700">
        <button class="cancel-btn" type="button" :disabled="page <= 1" @click="loadUsers(page - 1)">上一页</button>
        <span class="text-sm text-gray-600 dark:text-gray-400">第 {{ page }} 页 / 共 {{ totalPages }} 页</span>
        <button class="cancel-btn" type="button" :disabled="page >= totalPages" @click="loadUsers(page + 1)">下一页</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { assignTeacherAPI, getUsersAPI, revokeTeacherAPI } from "@/api/tenantConsole";
import { ROLE_LABELS, type TenantUser } from "@/types/tenantConsole";
import DemoNotice from "@/components/DemoNotice.vue";
import { useAuthStore } from "@/stores/auth";
import toaster from "@/utils/toaster";

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);

const users = ref<TenantUser[]>([]);
const query = ref("");
const page = ref(1);
const limit = ref(20);
const total = ref(0);

// Filter state
const roleFilter = ref<'all' | 'teacher' | 'student'>('all');
const subscriptionFilter = ref<'all' | 'active' | 'none' | 'expired'>('all');

// Statistics
const teacherCount = ref(0);
const activeSubscriptionCount = ref(0);
const newUsersThisMonth = ref(0);

// Teacher operation state
const busyUserId = ref<number | null>(null);
const statusMessage = ref("");
const statusType = ref<"success" | "error">("success");

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));

/**
 * 过滤用户列表
 */
const filteredUsers = computed(() => {
  let result = users.value;

  // Role filter
  if (roleFilter.value === 'teacher') {
    result = result.filter(u => u.role === 3);
  } else if (roleFilter.value === 'student') {
    result = result.filter(u => u.role !== 3);
  }

  // Subscription filter
  if (subscriptionFilter.value === 'active') {
    result = result.filter(u => u.activeSubscription?.status === 'ACTIVE');
  } else if (subscriptionFilter.value === 'none') {
    result = result.filter(u => !u.activeSubscription);
  } else if (subscriptionFilter.value === 'expired') {
    result = result.filter(u => u.activeSubscription?.status === 'EXPIRED');
  }

  return result;
});

const formatDate = (dateTime: string) => new Date(dateTime).toLocaleString("zh-CN");
const roleLabel = (role: number | null) => (role === null ? "未设置" : ROLE_LABELS[role] || `角色 ${role}`);
const roleBadgeClass = (role: number | null) => {
  const baseClass = "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium";
  if (role === 3) {
    return `${baseClass} bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300`;
  }
  return `${baseClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`;
};
const subscriptionLabel = (status: string) => {
  const statusMap: Record<string, string> = {
    ACTIVE: "有效",
    PENDING: "待激活",
    EXPIRED: "已过期",
    CANCELLED: "已取消",
  };
  return statusMap[status] || status;
};
const subscriptionBadgeClass = (status: string) => {
  const baseClass = "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium";
  if (status === "ACTIVE") {
    return `${baseClass} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`;
  }
  if (status === "PENDING") {
    return `${baseClass} bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300`;
  }
  return `${baseClass} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`;
};

/**
 * 计算统计数据
 */
const calculateStats = () => {
  teacherCount.value = users.value.filter(u => u.role === 3).length;
  activeSubscriptionCount.value = users.value.filter(
    u => u.activeSubscription?.status === 'ACTIVE'
  ).length;

  const currentMonth = new Date().toISOString().slice(0, 7); // "2026-03"
  newUsersThisMonth.value = users.value.filter(
    u => u.boundAt.startsWith(currentMonth)
  ).length;
};

/**
 * 加载用户列表。
 */
const loadUsers = async (nextPage = page.value) => {
  statusMessage.value = "";
  const response = await getUsersAPI({
    page: nextPage,
    limit: limit.value,
    query: query.value || undefined,
  });

  if (response.status !== 200 || !response.data) {
    return;
  }

  users.value = response.data.users;
  total.value = response.data.total;
  page.value = response.data.page;
  limit.value = response.data.limit;
  
  calculateStats();
};

/**
 * 将用户设置为教师
 */
const handleAssign = async (userId: number) => {
  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }
  
  busyUserId.value = userId;
  statusMessage.value = "";
  try {
    const response = await assignTeacherAPI(userId);
    statusType.value = response.status === 200 ? "success" : "error";
    statusMessage.value = response.message;
    await loadUsers(page.value);
  } finally {
    busyUserId.value = null;
  }
};

/**
 * 撤销教师身份
 */
const handleRevoke = async (userId: number) => {
  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }
  
  busyUserId.value = userId;
  statusMessage.value = "";
  try {
    const response = await revokeTeacherAPI(userId);
    statusType.value = response.status === 200 ? "success" : "error";
    statusMessage.value = response.message;
    await loadUsers(page.value);
  } finally {
    busyUserId.value = null;
  }
};

onMounted(() => {
  loadUsers(1);
});
</script>
